'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from 'cn';
import { CheckIcon, CopyIcon } from 'lucide-react';
import mergeRefs from 'merge-refs';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { getIconByLanguage } from '@/utils/get-icon-by-language';
import { Button } from './button';
import { ScrollArea } from './scroll-area';
type CodeBlockProps = React.ComponentProps<'pre'> & {
  'data-lang'?: string;
  figureClassName?: string;
  scrollAreaClassName?: string;
};
export function CodeBlock(props: CodeBlockProps) {
  const {
    children,
    className,
    title,
    'data-lang': lang,
    figureClassName,
    scrollAreaClassName,
    ref,
    ...rest
  } = props;
  const preRef = useRef<HTMLPreElement>(null);
  const Icon = getIconByLanguage(lang ?? '');
  async function onCopy() {
    try {
      await navigator.clipboard.writeText(preRef.current?.textContent ?? '');
    } catch (error) {
      console.error('Failed to copy code to clipboard:', error);
    }
  }
  return <figure className={cn('not-prose group relative my-6 overflow-hidden rounded-2xl border bg-input/30 text-sm', figureClassName)}>
      {title ? <div className='flex flex-row items-center gap-2 border-b bg-muted px-4 py-1.5'>
          <div className='text-muted-foreground'>
            <Icon className='size-3.5' />
          </div>
          <figcaption className='flex-1 truncate text-muted-foreground'>{title}</figcaption>
          <CopyButton className='-me-2' onCopy={onCopy} />
        </div> : <CopyButton className='absolute top-2 right-2 z-10' onCopy={onCopy} />}

      <ScrollArea className={scrollAreaClassName}>
        <pre ref={mergeRefs(preRef, ref)} className={cn('p-4 text-[13px]', className)} {...rest}>
          {children}
        </pre>
      </ScrollArea>
    </figure>;
}
type CopyButtonProps = React.ComponentProps<typeof Button> & {
  onCopy: () => void;
};
function CopyButton(props) {
  const $ = _c(21);
  let className;
  let onCopy;
  let rest;
  if ($[0] !== props) {
    ({
      onCopy,
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = onCopy;
    $[3] = rest;
  } else {
    className = $[1];
    onCopy = $[2];
    rest = $[3];
  }
  const [isCopied, setIsCopied] = useState(false);
  const t = useTranslations();
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const copyResetTimeoutId = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      return () => {
        clearTimeout(copyResetTimeoutId);
      };
    };
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isCopied) {
    t1 = [isCopied];
    $[5] = isCopied;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== onCopy) {
    t2 = () => {
      onCopy();
      setIsCopied(true);
    };
    $[7] = onCopy;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== className) {
    t3 = cn("size-7.5 opacity-0 transition-opacity group-hover:opacity-100", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t("common.aria-labels.copy-code");
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== isCopied) {
    t5 = isCopied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />;
    $[13] = isCopied;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== rest || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <Button variant="ghost" size="icon" onClick={t2} className={t3} aria-label={t4} {...rest}>{t5}</Button>;
    $[15] = rest;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}